import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Zap, Router, CheckCircle, XCircle, RefreshCw, AlertTriangle, Clock, Workflow, Lock, Unlock, History,
} from "lucide-react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { primeAudio, playSuccessChime } from "../lib/notificationSound";
import { automationApi } from "../lib/automationApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const WRITE_ROLES = ["super_admin", "admin", "customer_admin", "site_manager"];
const OUTPUT_SIGNAL_TYPES = ["digital_output", "binary_output", "analog_output"];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

// Minutes remaining on a manual override — spec §7 rail 2 (SPEC_AUTOMATION_CAUSE_EFFECT.md).
function fmtRemaining(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.ceil(ms / 60000);
  return mins < 60 ? `${mins}m left` : `${Math.floor(mins / 60)}h ${mins % 60}m left`;
}

// Relative freshness for a "Current Status" reading — a bare "OFF" doesn't tell an operator
// whether that's a live reading or a stale one from before the device dropped offline.
function fmtAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Keep only the most recent reading per (device, parameter) — NOT per parameter name alone.
// Register names are not unique across slaves under the same gateway (found 2026-08-24: an
// external relay board and the gateway's own built-in I/O both report "output"-style names
// independently), so deduping on parameter name alone silently collapses two different
// devices' registers into one, picking whichever updated most recently.
function dedupeRegisters(docs) {
  const map = new Map();
  for (const doc of docs) {
    const key = `${doc.device_id}::${doc.parameter}`;
    const existing = map.get(key);
    if (!existing || doc.ingested_at > existing.ingested_at) {
      map.set(key, doc);
    }
  }
  return Array.from(map.values());
}

function AckStatusBadge({ cmd }) {
  switch (cmd.ack_state) {
    case "acked_success":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
          Confirmed
        </span>
      );
    case "acked_failure":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold" title={cmd.device_error || undefined}>
          <XCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> Rejected
          {cmd.device_error && <span className="text-red-600 font-normal text-[10px]">({cmd.device_error})</span>}
        </span>
      );
    case "no_confirmation":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} /> No ACK
        </span>
      );
    case "pending":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold">
          <Clock className="w-3.5 h-3.5 animate-spin" strokeWidth={2.5} /> Pending…
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
          <XCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> Failed
        </span>
      );
  }
}

// Relay/equipment control only — no register-monitoring grid. See GatewayCommands.jsx for the
// full diagnostics page this was split from; that page keeps the Live Register Monitor, this one
// deliberately drops it so an operator focused purely on actuating equipment isn't wading through
// dozens of unrelated DI/AI cards to find the handful of controls that matter.
export default function ControlManagement() {
  const { selectedSite } = useSiteContext();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [gateway, setGateway] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [cmdLog, setCmdLog] = useState([]);
  const [writableRegs, setWritableRegs] = useState([]);
  const [analogInputs, setAnalogInputs] = useState({}); // tag_name -> typed value, for analog_output rows
  const [confirm, setConfirm] = useState(null); // { tag_name, value, label }
  const [sending, setSending] = useState(false);
  const [ruleCounts, setRuleCounts] = useState({}); // io_point_name -> # rules that can write it
  const [overrides, setOverrides] = useState({}); // tag_name -> override doc
  const [overrideBusy, setOverrideBusy] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [cmdLogError, setCmdLogError] = useState(null);

  const loadGateway = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const r = await api.get("/hardware/gateways");
      const gw = (r.data || []).find((g) => g.site_id === selectedSite);
      setGateway(gw || null);
    } catch { /* ignore */ }
  }, [selectedSite]);

  const loadStatus = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/v1/gateways/${gwId}/status`);
      setStatus(r.data);
    } catch { /* ignore */ } finally {
      setStatusChecked(true);
    }
  }, []);

  // Still needed even without the register grid — it's what "Current Status" on each control
  // card, and the success chime's change detection, read from.
  const loadTelemetry = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const r = await api.get(`/v1/sites/${selectedSite}/telemetry/latest`);
      setRegisters(dedupeRegisters(r.data || []));
    } catch { /* ignore */ }
  }, [selectedSite]);

  const loadCmdLog = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/v1/gateways/${gwId}/command-log`);
      setCmdLog(r.data || []);
      setCmdLogError(null);
    } catch (e) {
      // Surfaced in the Command Log modal — silently swallowing this made a failed fetch look
      // identical to "no commands sent yet".
      setCmdLogError(e.response?.data?.detail || e.message || "Could not load command log");
    }
  }, []);

  const loadWritableRegs = useCallback(async (gwId) => {
    if (!gwId) return;
    try {
      const r = await api.get(`/hardware/gateways/${gwId}/io-mappings`);
      const outputs = (r.data || [])
        .filter((m) => OUTPUT_SIGNAL_TYPES.includes(m.signal_type))
        .map((m) => ({
          tag_name: m.io_point_address,
          readback_tag_name: m.readback_io_point_name || m.io_point_address,
          // Which slave's telemetry the readback register actually lives under — register
          // names collide across slaves (2026-08-24), so without this a lookup by name alone
          // can silently read a completely unrelated device's state.
          device_id: m.device_id,
          label: m.io_point_name || m.io_point_address,
          io_point_name: m.io_point_name,
          signal_type: m.signal_type,
          unit: m.engineering_unit,
          write_mode: m.write_mode || "tag",
          // Shown on the card and in the confirmation dialog so the operator can see which
          // physical relay they're about to actuate, not just an opaque point name.
          addressing:
            m.write_mode === "direct"
              ? `slave ${m.modbus_slave_id} · coil ${m.modbus_start_address} · FC${m.modbus_function_code ?? 15}`
              : null,
        }));
      setWritableRegs(outputs);
    } catch { /* ignore */ }
  }, []);

  // Which output points have a Cause & Effect rule that can drive them, and whether any are
  // currently under manual override — badges on Control Management's relay cards (spec §10.4 /
  // SPEC_AUTOMATION_BACKEND_CHANGES.md §7.3). Degrades to "no rules" quietly if the automation
  // API isn't deployed — automationApi's call() wrapper already handles that.
  const loadAutomation = useCallback(async (gwId) => {
    if (!gwId || !selectedSite) return;
    try {
      const [rulesRes, overridesRes, assetsRes] = await Promise.all([
        automationApi.listRules({ site_id: selectedSite }),
        automationApi.listOverrides(gwId),
        api.get("/assets", { params: { site_id: selectedSite } }).catch(() => ({ data: [] })),
      ]);
      const assets = assetsRes.data || [];
      const assetById = new Map(assets.map((a) => [a.asset_id, a]));

      const counts = {};
      for (const rule of rulesRes.ok ? rulesRes.data || [] : []) {
        for (const action of rule.actions || []) {
          if (action.type !== "write" || !action.target) continue;
          let ioPoint = null;
          if (action.target.kind === "io" && action.target.gateway_id === gwId) {
            ioPoint = action.target.io_point_name;
          } else if (action.target.kind === "asset") {
            const asset = assetById.get(action.target.asset_id);
            if (asset && asset.gateway_id === gwId) ioPoint = asset.io_point_name;
          }
          if (ioPoint) counts[ioPoint] = (counts[ioPoint] || 0) + 1;
        }
      }
      setRuleCounts(counts);

      const byTag = {};
      for (const o of overridesRes.ok ? overridesRes.data || [] : []) {
        byTag[o.io_point_name] = o;
      }
      setOverrides(byTag);
    } catch { /* ignore — badge is informational, never blocks the control panel */ }
  }, [selectedSite]);

  const toggleOverride = async (reg) => {
    if (!gateway) return;
    setOverrideBusy(reg.tag_name);
    const active = overrides[reg.tag_name];
    const r = await automationApi.setOverride({
      gateway_id: gateway.gateway_id,
      io_point_name: reg.tag_name,
      action: active ? "clear" : "set",
    });
    setOverrideBusy(null);
    if (!r.ok) { toast.error(active ? "Could not return to auto" : "Could not set override"); return; }
    toast.success(active ? `${reg.label} returned to automatic control` : `${reg.label} is now under manual override`);
    loadAutomation(gateway.gateway_id);
  };

  // Reset + load gateway whenever site changes
  useEffect(() => {
    setGateway(null);
    setStatus(null);
    setStatusChecked(false);
    setRegisters([]);
    setCmdLog([]);
    setWritableRegs([]);
    setRuleCounts({});
    setOverrides({});
    lastValuesRef.current = {};
    loadGateway();
  }, [loadGateway]);

  // Poll everything every 5 s once gateway is known, including writable regs — an IO mapping
  // (e.g. a relay's write tag) can be edited from another tab/page at any time, and a stale
  // cached tag_name here would silently drive commands at the wrong register with no visible
  // sign anything was wrong (see 2026-08-24 incident: fixed the mapping, kept sending the old
  // tag for another 10+ minutes because this list wasn't refetched until the page reloaded).
  useEffect(() => {
    if (!gateway) return;
    loadStatus(gateway.gateway_id);
    loadTelemetry();
    loadCmdLog(gateway.gateway_id);
    loadWritableRegs(gateway.gateway_id);
    loadAutomation(gateway.gateway_id);
    const i = setInterval(() => {
      loadStatus(gateway.gateway_id);
      loadTelemetry();
      loadCmdLog(gateway.gateway_id);
      loadWritableRegs(gateway.gateway_id);
      loadAutomation(gateway.gateway_id);
    }, 5000);
    return () => clearInterval(i);
  }, [gateway, loadStatus, loadTelemetry, loadCmdLog, loadWritableRegs, loadAutomation]);

  const sendCommand = async () => {
    if (!confirm || !gateway) return;
    // Unlocks audio playback for the later, poll-driven success chime — browsers require a user
    // gesture before any sound can play, and this click is one; the chime itself is NOT played
    // here, only primed for when a subsequent poll confirms the status actually changed.
    primeAudio();
    setSending(true);
    try {
      await api.post(`/v1/gateways/${gateway.gateway_id}/write`, {
        tag_name: confirm.tag_name,
        value: confirm.value,
      });
      const displayVal = confirm.value === "1" ? "ON" : confirm.value === "0" ? "OFF" : confirm.value;
      toast.success(`Command sent — ${confirm.label} set to ${displayVal}`);
      loadCmdLog(gateway.gateway_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "device_not_connected") {
        toast.error("Device is not connected — command not delivered");
      } else {
        toast.error(detail || "Command failed");
      }
    } finally {
      setSending(false);
      setConfirm(null);
    }
  };

  // Full reading (value + when it was reported) — used to show freshness on the control cards,
  // since "OFF" alone doesn't say whether that's live or from before the device dropped offline.
  // Matched on device_id as well as parameter name: register names collide across slaves under
  // the same gateway (found 2026-08-24 — a relay board and the gateway's own built-in I/O both
  // report "output"-style names independently), so matching on name alone can silently read a
  // completely unrelated device's state. Falls back to name-only if device_id wasn't set on the
  // mapping (older mappings created before this field was consistently populated).
  const currentReading = (tag_name, device_id) => {
    const candidates = registers.filter((r) => r.parameter === tag_name);
    if (device_id) {
      const scoped = candidates.find((r) => r.device_id === device_id);
      if (scoped) return scoped;
    }
    return candidates[0] || null;
  };

  // Chime on a confirmed status change, not on the click that requested it — the click only
  // sends a command; this fires later, once a subsequent poll shows the device's own readback
  // actually moved. lastValuesRef is empty on first load / site switch, so the first reading of
  // any control never fires a chime — only a genuine transition from an already-known value does.
  const lastValuesRef = useRef({});
  useEffect(() => {
    if (!writableRegs.length) return;
    const prev = lastValuesRef.current;
    const next = {};
    let anyChanged = false;
    for (const reg of writableRegs) {
      const reading = currentReading(reg.readback_tag_name, reg.device_id);
      if (!reading) continue;
      const key = `${reg.device_id || ""}::${reg.readback_tag_name}`;
      next[key] = reading.value;
      if (key in prev && prev[key] !== reading.value) anyChanged = true;
    }
    lastValuesRef.current = next;
    if (anyChanged) playSuccessChime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registers, writableRegs]);

  const connected = status?.connected === true;

  if (!selectedSite) {
    return (
      <div className="p-8 text-gray-500 text-center">
        Select a site to view gateway data.
      </div>
    );
  }

  // Latest command per write tag — cmdLog comes back newest-first, so the first hit wins. Shown
  // inline on each control card so the Pending → Confirmed feedback after a click stays visible
  // without the full audit log (now in a modal) taking up the page.
  const lastCmdByTag = {};
  for (const cmd of cmdLog) {
    if (!(cmd.tag_name in lastCmdByTag)) lastCmdByTag[cmd.tag_name] = cmd;
  }

  return (
    <div className="space-y-3">

      {/* ── Header — one compact row ── */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Control Management</h1>
          <span className="text-[10px] text-orange-600 uppercase tracking-wider font-bold inline-flex items-center gap-1 shrink-0">
            <Router className="w-3 h-3" strokeWidth={2.5} /> Gateway Remote Control
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs">
          {gateway && status && (
            <>
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold ${
                  !statusChecked
                    ? "bg-gray-100 border border-gray-200 text-gray-600"
                    : connected
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : "bg-red-50 border border-red-200 text-red-700"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    !statusChecked
                      ? "bg-gray-400 animate-pulse"
                      : connected
                      ? "bg-emerald-500 status-pulse-green"
                      : "bg-red-500"
                  }`}
                />
                {!statusChecked ? "Checking connection…" : connected ? "Device Connected" : "Device Offline"}
              </div>
              <div className="glass px-2.5 py-1 rounded-lg text-gray-500 flex items-center gap-2 min-w-0">
                <span className="text-gray-900 font-medium truncate max-w-[220px]" title={status.gateway_name || undefined}>
                  {status.gateway_name || status.hardware_model || gateway.gateway_id}
                </span>
                {status.peer_ip && <span>· IP <span className="font-mono">{status.peer_ip}</span></span>}
                {status.last_seen_at && <span>· Last seen <span className="font-medium">{fmtTime(status.last_seen_at)}</span></span>}
              </div>
            </>
          )}
          {gateway && (
            <button
              type="button"
              onClick={() => setLogOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition"
            >
              <History className="w-3.5 h-3.5" strokeWidth={2.5} /> Command Log
              {cmdLog.length > 0 && <span className="px-1.5 rounded-full bg-gray-100 text-gray-600 text-[10px]">{Math.min(cmdLog.length, 20)}</span>}
            </button>
          )}
        </div>
      </div>

      {/* ── No gateway registered ── */}
      {!gateway && (
        <div className="glass rounded-lg p-8 text-center text-gray-500">
          <Router className="w-8 h-8 mx-auto mb-3 opacity-30" strokeWidth={2.5} />
          <p>No hardware gateway registered for this site.</p>
          <p className="text-xs mt-1">Add one in Hardware Gateways → Management.</p>
        </div>
      )}

      {gateway && canWrite && (
        <section className="space-y-3">
          {/* Connection state + critical-action warning, merged into one thin strip */}
          {!statusChecked ? (
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm text-gray-600">
              <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" strokeWidth={2} /> Checking device connection…
            </div>
          ) : !connected ? (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm">
              <XCircle className="w-4 h-4 text-red-600 shrink-0" strokeWidth={2.5} />
              <span className="text-red-900 font-semibold">Device Offline</span>
              <span className="text-red-700">— commands are disabled until the device reconnects.</span>
            </div>
          ) : null}

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" strokeWidth={2.5} />
            <span>
              <span className="font-semibold text-amber-900">Critical actions:</span> every command actuates a physical relay on
              fire safety equipment, is audit-logged and <span className="font-bold">cannot be reversed</span>.
            </span>
          </div>

          {connected && !writableRegs.length && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
              <Zap className="w-7 h-7 mx-auto mb-2 text-gray-300" strokeWidth={2} />
              <p className="text-gray-600 font-medium">No Writable Registers</p>
              <p className="text-gray-500 text-sm mt-1">
                Add output signal types (Digital/Binary/Analog) in Hardware Gateways → IO Mappings
              </p>
            </div>
          )}

          {connected && writableRegs.length > 0 && (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))" }}>
              {writableRegs.map((reg) => {
                const reading = currentReading(reg.readback_tag_name, reg.device_id);
                const val = reading ? reading.value : null;
                const ago = reading ? fmtAgo(reading.timestamp) : null;
                const isAnalog = reg.signal_type === "analog_output";
                const readsBackUnderDifferentName = reg.readback_tag_name !== reg.tag_name;
                const ruleCount = ruleCounts[reg.io_point_name] || 0;
                const override = overrides[reg.tag_name];
                const lastCmd = lastCmdByTag[reg.tag_name];
                return (
                  <div key={reg.tag_name} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition flex flex-col gap-2">
                    <div className="min-w-0">
                      <div className="text-gray-900 font-semibold text-sm truncate" title={reg.label}>{reg.label}</div>
                      <div className="text-[10px] text-gray-500 truncate">
                        Writes <span className="font-mono text-gray-700">{reg.tag_name}</span>
                        {readsBackUnderDifferentName && (
                          <> · reads <span className="font-mono text-gray-700">{reg.readback_tag_name}</span></>
                        )}
                      </div>
                      {reg.addressing && (
                        <div className="text-[10px] text-orange-600 font-mono truncate">{reg.addressing}</div>
                      )}
                      {(ruleCount > 0 || override) && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {ruleCount > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-px rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-semibold"
                              title={`${ruleCount} automation rule${ruleCount === 1 ? "" : "s"} can control this point`}
                            >
                              <Workflow className="w-3 h-3" strokeWidth={2.5} />
                              {ruleCount} rule{ruleCount === 1 ? "" : "s"}
                            </span>
                          )}
                          {override && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-px rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold">
                              <Lock className="w-3 h-3" strokeWidth={2.5} />
                              Override{fmtRemaining(override.expires_at) ? ` · ${fmtRemaining(override.expires_at)}` : ""}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {val !== null ? (
                      <div className={`flex items-center justify-between px-2 py-1.5 rounded-lg border ${
                        isAnalog
                          ? "bg-sky-50 border-sky-200"
                          : val === 1
                          ? "bg-emerald-50 border-emerald-200"
                          : "bg-slate-50 border-slate-200"
                      }`}>
                        <span className="text-[11px] text-gray-600">
                          Current Status
                          {ago && <span className="text-gray-400"> · {ago}</span>}
                        </span>
                        <span className={`font-bold text-sm ${
                          isAnalog
                            ? "text-sky-700"
                            : val === 1
                            ? "text-emerald-700"
                            : "text-red-600"
                        }`}>
                          {isAnalog ? `${val}${reg.unit ? ` ${reg.unit}` : ""}` : val === 1 ? "ON" : "OFF"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between px-2 py-1.5 rounded-lg border bg-amber-50 border-amber-200">
                        <span className="text-[11px] text-amber-700">Current Status</span>
                        <span className="font-bold text-xs text-amber-700" title={`No telemetry has been reported yet for "${reg.readback_tag_name}"`}>
                          No data yet
                        </span>
                      </div>
                    )}

                    {isAnalog ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          disabled={!connected}
                          value={analogInputs[reg.tag_name] ?? ""}
                          onChange={(e) => setAnalogInputs({ ...analogInputs, [reg.tag_name]: e.target.value })}
                          placeholder={reg.unit || "value"}
                          className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-sm disabled:opacity-50"
                        />
                        <Button
                          size="sm"
                          disabled={!connected || !analogInputs[reg.tag_name]}
                          onClick={() =>
                            setConfirm({ tag_name: reg.tag_name, value: String(analogInputs[reg.tag_name]), label: reg.label, addressing: reg.addressing })
                          }
                          className="bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"
                        >
                          Set
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={!connected || val === 1}
                          onClick={() =>
                            setConfirm({ tag_name: reg.tag_name, value: "1", label: reg.label, addressing: reg.addressing })
                          }
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {val === 1 ? "Already ON" : "Turn ON"}
                        </Button>
                        <Button
                          size="sm"
                          disabled={!connected || val === 0}
                          onClick={() =>
                            setConfirm({ tag_name: reg.tag_name, value: "0", label: reg.label, addressing: reg.addressing })
                          }
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {val === 0 ? "Already OFF" : "Turn OFF"}
                        </Button>
                      </div>
                    )}

                    {canWrite && (ruleCount > 0 || override) && (
                      <button
                        type="button"
                        disabled={overrideBusy === reg.tag_name}
                        onClick={() => toggleOverride(reg)}
                        className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1 rounded-lg border transition disabled:opacity-50 disabled:cursor-not-allowed
                          border-gray-200 text-gray-600 hover:bg-gray-50"
                      >
                        {override
                          ? (<><Unlock className="w-3 h-3" strokeWidth={2.5} /> Return to auto</>)
                          : (<><Lock className="w-3 h-3" strokeWidth={2.5} /> Manual override</>)}
                      </button>
                    )}

                    {lastCmd && (
                      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-100 text-[10px] text-gray-500">
                        <span className="truncate">
                          Last: <span className="font-bold text-gray-700">
                            {lastCmd.value === "1" ? "ON" : lastCmd.value === "0" ? "OFF" : lastCmd.value}
                          </span> · {fmtAgo(lastCmd.issued_at) || fmtTime(lastCmd.issued_at)}
                        </span>
                        <span className="scale-90 origin-right shrink-0"><AckStatusBadge cmd={lastCmd} /></span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── Command Audit Log (modal) ── */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Command Audit Log</DialogTitle>
            <p className="text-xs text-gray-500">All remote commands sent to this device · Last 20 entries</p>
          </DialogHeader>
          <div className="border border-gray-200 rounded-lg overflow-auto max-h-[65vh]">
            {cmdLogError && cmdLog.length === 0 ? (
              <div className="p-10 text-center">
                <XCircle className="w-8 h-8 mx-auto mb-3 text-red-700" strokeWidth={2} />
                <p className="text-red-700 font-medium">Could not load command log</p>
                <p className="text-gray-500 text-sm mt-1">{String(cmdLogError)}</p>
                {gateway && <p className="text-gray-400 text-xs mt-1 font-mono">Gateway {gateway.gateway_id}</p>}
              </div>
            ) : cmdLog.length === 0 ? (
              <div className="p-10 text-center">
                <Zap className="w-8 h-8 mx-auto mb-3 text-gray-300" strokeWidth={2} />
                <p className="text-gray-600 font-medium">No Commands Sent Yet</p>
                <p className="text-gray-500 text-sm mt-1">Commands will appear here as you send them</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="border-b border-gray-200 bg-gray-50 text-gray-700 text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-semibold">Time</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Register</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Value</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Issued By</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cmdLog.slice(0, 20).map((cmd) => (
                    <tr key={cmd.command_id} className="border-b border-gray-200 hover:bg-gray-50 transition">
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs whitespace-nowrap">{fmtTime(cmd.issued_at)}</td>
                      <td className="px-4 py-2.5 text-gray-900 font-mono text-xs font-semibold">
                        {cmd.tag_name}
                        {cmd.modbus && (
                          <div
                            className="text-[10px] text-gray-500 font-normal"
                            title={cmd.modbus.raw_frame ? `Wire frame: ${cmd.modbus.raw_frame}` : undefined}
                          >
                            slave {cmd.modbus.slave_id} · coil {cmd.modbus.start_address} · FC{cmd.modbus.function_code}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-bold">
                        <span className={cmd.value === "1" ? "text-emerald-700" : cmd.value === "0" ? "text-red-700" : "text-sky-700"}>
                          {cmd.value === "1" ? "ON" : cmd.value === "0" ? "OFF" : cmd.value}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{cmd.issued_by}</td>
                      <td className="px-4 py-2.5"><AckStatusBadge cmd={cmd} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Confirmation AlertDialog ── */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !sending && setConfirm(null)}>
        <AlertDialogContent className="bg-white border-gray-200 text-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Relay Command</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 space-y-2">
              <span className="block">
                Send{" "}
                <code className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded text-xs">
                  {confirm?.tag_name} = {confirm?.value}
                </code>{" "}
                to{" "}
                <span className="text-gray-900 font-medium">
                  {status?.gateway_name || gateway?.gateway_id}
                </span>
                ?
              </span>
              {confirm?.addressing && (
                <span className="block text-xs">
                  Target:{" "}
                  <code className="bg-gray-100 text-gray-900 px-1.5 py-0.5 rounded font-mono">
                    {confirm.addressing}
                  </code>
                </span>
              )}
              <span className="block text-amber-400/80">
                This will actuate a physical relay on the fire panel. The action is
                irreversible and audit-logged.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-gray-200 text-gray-600 hover:text-gray-900 bg-transparent"
              onClick={() => setConfirm(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={sending}
              onClick={sendCommand}
              className="bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Command"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
